import { type Vec, EPS, dist, dist2, closestOnSegment, segmentIntersection, lerp } from './vec';

/** Closed ring of points. The closing edge (last -> first) is implicit; the first point is not repeated. */
export type Ring = Vec[];
/** Open sequence of points. */
export type Polyline = Vec[];

/** A simple polygon with optional holes. Outer ring is counter-clockwise in a y-up sense (positive signed area here). */
export interface PolygonWithHoles {
  outer: Ring;
  holes: Ring[];
}

export interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export const emptyBBox = (): BBox => ({ minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

export function bboxOf(points: Iterable<Vec>, into: BBox = emptyBBox()): BBox {
  for (const p of points) {
    if (p.x < into.minX) into.minX = p.x;
    if (p.y < into.minY) into.minY = p.y;
    if (p.x > into.maxX) into.maxX = p.x;
    if (p.y > into.maxY) into.maxY = p.y;
  }
  return into;
}

export function bboxIsEmpty(b: BBox): boolean {
  return !(b.maxX >= b.minX && b.maxY >= b.minY);
}

export function bboxUnion(a: BBox, b: BBox): BBox {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** Signed area (shoelace). Positive for counter-clockwise rings in a y-up coordinate system. */
export function signedArea(ring: Ring): number {
  let a = 0;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    a += (ring[j].x * ring[i].y - ring[i].x * ring[j].y);
  }
  return a / 2;
}

export const ringArea = (ring: Ring): number => Math.abs(signedArea(ring));

export function polygonArea(p: PolygonWithHoles): number {
  return ringArea(p.outer) - p.holes.reduce((s, h) => s + ringArea(h), 0);
}

export function ringPerimeter(ring: Ring): number {
  let l = 0;
  for (let i = 0; i < ring.length; i++) l += dist(ring[i], ring[(i + 1) % ring.length]);
  return l;
}

export function polylineLength(line: Polyline): number {
  let l = 0;
  for (let i = 1; i < line.length; i++) l += dist(line[i - 1], line[i]);
  return l;
}

/** Returns a copy with the requested orientation (positive signed area when `positive`). */
export function orient(ring: Ring, positive: boolean): Ring {
  const a = signedArea(ring);
  return (a >= 0) === positive ? ring.slice() : ring.slice().reverse();
}

/** Normalizes orientation: outer positive area, holes negative area. */
export function normalizePolygon(p: PolygonWithHoles): PolygonWithHoles {
  return { outer: orient(p.outer, true), holes: p.holes.map((h) => orient(h, false)) };
}

/** Removes consecutive duplicate points (and a duplicated closing point for rings). */
export function dedupe(points: Vec[], closed: boolean, eps = 1e-4): Vec[] {
  const out: Vec[] = [];
  for (const p of points) {
    if (out.length === 0 || dist2(out[out.length - 1], p) > eps * eps) out.push(p);
  }
  if (closed && out.length > 1 && dist2(out[0], out[out.length - 1]) <= eps * eps) out.pop();
  return out;
}

/** Even-odd point in ring test. Points exactly on the boundary may go either way. */
export function pointInRing(p: Vec, ring: Ring): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = ring[i];
    const b = ring[j];
    if (a.y > p.y !== b.y > p.y) {
      const x = ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
      if (p.x < x) inside = !inside;
    }
  }
  return inside;
}

export function pointInPolygon(p: Vec, poly: PolygonWithHoles): boolean {
  if (!pointInRing(p, poly.outer)) return false;
  for (const h of poly.holes) if (pointInRing(p, h)) return false;
  return true;
}

export function polygonRings(p: PolygonWithHoles): Ring[] {
  return [p.outer, ...p.holes];
}

/** Iterates every edge of every ring as [a, b, ringIndex, edgeIndex]. */
export function* polygonEdges(rings: Ring[]): Generator<[Vec, Vec, number, number]> {
  for (let r = 0; r < rings.length; r++) {
    const ring = rings[r];
    for (let i = 0; i < ring.length; i++) yield [ring[i], ring[(i + 1) % ring.length], r, i];
  }
}

/** Minimum distance from point to any edge of the rings. */
export function distanceToRings(p: Vec, rings: Ring[]): number {
  let best = Infinity;
  for (const [a, b] of polygonEdges(rings)) {
    const d = dist(p, closestOnSegment(p, a, b));
    if (d < best) best = d;
  }
  return best;
}

/**
 * Detects self intersections of a ring (non-adjacent edges crossing).
 * O(n²) with bounding-box rejection, adequate for the edited shapes (typically < 2 000 vertices).
 * Returns the intersection points (capped at `limit`).
 */
export function ringSelfIntersections(ring: Ring, limit = 20): Vec[] {
  const n = ring.length;
  const found: Vec[] = [];
  if (n < 4) return found;
  for (let i = 0; i < n; i++) {
    const a1 = ring[i];
    const a2 = ring[(i + 1) % n];
    const minAx = Math.min(a1.x, a2.x), maxAx = Math.max(a1.x, a2.x);
    const minAy = Math.min(a1.y, a2.y), maxAy = Math.max(a1.y, a2.y);
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue; // adjacent through the closing edge
      const b1 = ring[j];
      const b2 = ring[(j + 1) % n];
      if (Math.max(b1.x, b2.x) < minAx || Math.min(b1.x, b2.x) > maxAx) continue;
      if (Math.max(b1.y, b2.y) < minAy || Math.min(b1.y, b2.y) > maxAy) continue;
      const hit = segmentIntersection(a1, a2, b1, b2);
      if (hit && hit.t > EPS && hit.t < 1 - EPS && hit.u > EPS && hit.u < 1 - EPS) {
        found.push(hit.point);
        if (found.length >= limit) return found;
      }
    }
  }
  return found;
}

/** Location on a ring expressed as edge index + parameter, with cumulative arc length. */
export interface RingLocation {
  edge: number;
  t: number;
  point: Vec;
  arc: number;
  distance: number;
}

export function cumulativeRingLengths(ring: Ring): number[] {
  const cum = [0];
  for (let i = 0; i < ring.length; i++) cum.push(cum[i] + dist(ring[i], ring[(i + 1) % ring.length]));
  return cum;
}

export function closestOnRing(p: Vec, ring: Ring, cum = cumulativeRingLengths(ring)): RingLocation {
  let best: RingLocation = { edge: 0, t: 0, point: ring[0], arc: 0, distance: Infinity };
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const c = closestOnSegment(p, a, b);
    const d = dist(p, c);
    if (d < best.distance) {
      const segLen = cum[i + 1] - cum[i];
      const t = segLen > EPS ? dist(a, c) / segLen : 0;
      best = { edge: i, t, point: c, arc: cum[i] + t * segLen, distance: d };
    }
  }
  return best;
}

/** Point at arc length `s` (wrapped) along a ring. */
export function pointAtRingArc(ring: Ring, s: number, cum = cumulativeRingLengths(ring)): Vec {
  const total = cum[cum.length - 1];
  if (total <= EPS) return ring[0];
  let x = s % total;
  if (x < 0) x += total;
  // binary search for the edge
  let lo = 0;
  let hi = ring.length - 1;
  while (lo < hi) {
    const m = (lo + hi + 1) >> 1;
    if (cum[m] <= x) lo = m;
    else hi = m - 1;
  }
  const segLen = cum[lo + 1] - cum[lo];
  const t = segLen > EPS ? (x - cum[lo]) / segLen : 0;
  return lerp(ring[lo], ring[(lo + 1) % ring.length], t);
}

/**
 * Walks along a ring from location `from` to `to` in the shorter direction and returns
 * the polyline (including both end points).
 */
export function walkRing(ring: Ring, from: RingLocation, to: RingLocation, cum = cumulativeRingLengths(ring)): Polyline {
  const total = cum[cum.length - 1];
  let forward = to.arc - from.arc;
  if (forward < 0) forward += total;
  const backward = total - forward;
  const out: Vec[] = [from.point];
  const n = ring.length;
  if (forward <= backward) {
    // vertices after from.edge up to to.edge
    let i = (from.edge + 1) % n;
    let steps = 0;
    const sameEdgeAhead = from.edge === to.edge && to.t >= from.t;
    if (!sameEdgeAhead) {
      while (steps < n) {
        out.push(ring[i]);
        if (i === to.edge) break;
        i = (i + 1) % n;
        steps++;
      }
    }
  } else {
    let i = from.edge;
    let steps = 0;
    const sameEdgeBehind = from.edge === to.edge && to.t <= from.t;
    if (!sameEdgeBehind) {
      while (steps < n) {
        out.push(ring[i]);
        const next = (i - 1 + n) % n;
        if (next === to.edge) {
          out.push(ring[(to.edge + 1) % n]);
          break;
        }
        i = next;
        steps++;
      }
    }
  }
  out.push(to.point);
  return dedupe(out, false);
}

/** Centroid of a ring (area weighted). Falls back to vertex average for degenerate rings. */
export function ringCentroid(ring: Ring): Vec {
  const a = signedArea(ring);
  if (Math.abs(a) < EPS) {
    const s = ring.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    return { x: s.x / Math.max(1, ring.length), y: s.y / Math.max(1, ring.length) };
  }
  let cx = 0;
  let cy = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const f = ring[j].x * ring[i].y - ring[i].x * ring[j].y;
    cx += (ring[j].x + ring[i].x) * f;
    cy += (ring[j].y + ring[i].y) * f;
  }
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

/** Does the open segment a-b cross any ring edge (touching endpoints excluded)? */
export function segmentCrossesRings(a: Vec, b: Vec, rings: Ring[], tol = 1e-4): boolean {
  for (const [p, q] of polygonEdges(rings)) {
    const hit = segmentIntersection(a, b, p, q);
    if (hit && hit.t > tol && hit.t < 1 - tol) return true;
  }
  return false;
}
